import { apolloClient } from '@/app/providers/apollo';
import { upstreamStatus } from './auth.service';
import {
  AccountStatusDocument,
  CheckCodeDocument,
  InviteCompleteDocument,
  InviteStartDocument,
  ResetPasswordCodeDocument,
  ResetPasswordWithCodeDocument,
} from '../__generated__/graphql';

export type AccountStatus = 'login' | 'invite' | 'unknown';
export type CodeFlowFailure = 'invalid_code' | 'locked' | 'error';

const toCodeFlowFailure = (err: unknown): CodeFlowFailure => {
  const status = upstreamStatus(err);
  if (status === 429) {
    return 'locked';
  }
  if (status === 400) {
    return 'invalid_code';
  }
  return 'error';
};

export const fetchAccountStatus = async (email: string): Promise<AccountStatus | null> => {
  try {
    const result = await apolloClient.mutate({
      mutation: AccountStatusDocument,
      variables: { input: { email } },
    });
    return result.data?.accountStatus?.status ?? null;
  } catch {
    return null;
  }
};

export const startInvite = async (email: string): Promise<boolean> => {
  try {
    const result = await apolloClient.mutate({
      mutation: InviteStartDocument,
      variables: { input: { email } },
    });
    return result.data?.inviteStart?.ok ?? false;
  } catch {
    return false;
  }
};

export const requestResetCode = async (email: string): Promise<boolean> => {
  try {
    const result = await apolloClient.mutate({
      mutation: ResetPasswordCodeDocument,
      variables: { input: { email } },
    });
    return result.data?.resetPasswordCode?.ok ?? false;
  } catch {
    return false;
  }
};

export const completeInvite = async (input: {
  email: string;
  code: string;
  password: string;
}): Promise<{ ok: true } | { ok: false; failure: CodeFlowFailure }> => {
  try {
    const result = await apolloClient.mutate({
      mutation: InviteCompleteDocument,
      variables: { input },
    });
    return result.data?.inviteComplete?.ok ? { ok: true } : { ok: false, failure: 'error' };
  } catch (err) {
    return { ok: false, failure: toCodeFlowFailure(err) };
  }
};

export const resetPasswordWithCode = async (input: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ ok: true } | { ok: false; failure: CodeFlowFailure }> => {
  try {
    const result = await apolloClient.mutate({
      mutation: ResetPasswordWithCodeDocument,
      variables: {
        input: { email: input.email, code: input.code, new_password: input.newPassword },
      },
    });
    return result.data?.resetPasswordWithCode?.ok ? { ok: true } : { ok: false, failure: 'error' };
  } catch (err) {
    return { ok: false, failure: toCodeFlowFailure(err) };
  }
};

export const checkLoginCode = async (input: {
  email: string;
  code: string;
  purpose: 'invite' | 'password_reset';
}): Promise<{ ok: true } | { ok: false; failure: CodeFlowFailure }> => {
  try {
    const result = await apolloClient.mutate({
      mutation: CheckCodeDocument,
      variables: { input },
    });
    return result.data?.checkCode?.ok ? { ok: true } : { ok: false, failure: 'error' };
  } catch (err) {
    return { ok: false, failure: toCodeFlowFailure(err) };
  }
};
